import { Injectable } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, FormControl, ValidatorFn, AsyncValidatorFn, Validators } from '@angular/forms';
import { NormalizedFieldState, NormalizedFormState, NormalizedSection } from '../config.models';
import { Store } from '@ngrx/store';
import { filter, map, Observable, Subscription } from 'rxjs';
import * as fromSelectors from '../selectors';
import { setFormValidity, updateFieldValueByPath } from '../actions';
import { ValidatorRegistryService } from './validator-registry.service';
import { ValidationRule, ValidationRuleType } from '../models/validator-models';
import { LocationCardState } from '../reducers';
import { FieldType, NumberField } from '../models/field-models';

@Injectable({
    providedIn: 'root',
})
export class DynamicFormBuilderService {
    private subscriptions: Subscription[] = [];

    private customValidatorFields: FormControl[] = [];
    constructor(
        private fb: FormBuilder,
        private store: Store,
        private validatorRegistry: ValidatorRegistryService,
    ) {}

    getFormFromStore(): Observable<FormGroup> {
        return this.store.select(fromSelectors.selectLocationCardState).pipe(
            filter((state) => !!state && !!state.formState),
            map((state) => this.buildFormFromConfig(state)),
        );
    }

    /**
     * Builds an Angular FormGroup from a normalized form configuration
     */
    buildFormFromConfig(state: LocationCardState<unknown>): FormGroup {
        this.unsubscribeAll();

        const form = this.fb.group({});
        const formState = state.formState;
        formState.config.sections.forEach((section) => {
            const sectionControl = this.processSection(section, state);
            form.addControl(section.id, sectionControl);
        });

        this.store.dispatch(setFormValidity({ valid: form.valid }));

        this.subscriptions.push(
            form.statusChanges.subscribe((status) => {
                this.store.dispatch(setFormValidity({ valid: status === 'VALID' }));
            }),
        );

        return form;
    }

    /**
     * Process a section and return either a FormGroup (standard) or FormArray (array type)
     */
    private processSection(section: NormalizedSection, state: LocationCardState<unknown>): FormGroup | FormArray {
        if (section.type === 'array') {
            return this.processArraySection(section, state);
        } else {
            return this.processStandardSection(section, state);
        }
    }

    /**
     * Process a standard section into a FormGroup with fields as controls
     */
    private processStandardSection(section: NormalizedSection, state: LocationCardState<unknown>): FormGroup {
        const sectionGroup = this.fb.group({});

        // Add regular field controls directly to the section group
        Object.entries(section.fields).forEach(([fieldId, field]) => {
            // Skip section fields - they'll be handled separately
            if (field.type !== 'section') {
                sectionGroup.addControl(fieldId, this.createFormControl(field, state));
            } else if (field.section) {
                // For section fields, process the nested section
                const nestedControl = this.processSection(field.section, state);
                sectionGroup.addControl(fieldId, nestedControl);
            }
        });

        if (section.errors && Object.keys(section.errors).length > 0) {
            const errors = Object.keys(section.errors).reduce((acc, key) => {
                acc[key] = true;

                return acc;
            }, {});

            sectionGroup.setErrors(errors);
        }

        return sectionGroup;
    }

    /**
     * Process an array section into a FormArray containing FormGroups for each row
     */
    private processArraySection(section: NormalizedSection, state: LocationCardState<unknown>): FormArray {
        // Explicitly type the FormArray to accept FormGroups
        const arrayControl = new FormArray<FormGroup>([]);

        // Create a form group for each row in the array section
        section.rows.forEach((row, rowIndex) => {
            const rowGroup = this.fb.group({});

            // Add controls for each field in this row
            row.fields.forEach((fieldId) => {
                const field = section.fields[fieldId];
                if (field) {
                    if (field.type === 'section') {
                        if (field.section) {
                            const sectionCopy = { ...field.section };
                            this.updatePathsInSection(sectionCopy, rowIndex);
                            rowGroup.addControl(fieldId, this.processSection(sectionCopy, state));
                        }
                    } else {
                        const fieldCopy = { ...field };
                        if (fieldCopy.path) {
                            fieldCopy.path = fieldCopy.path.replace(/\[\d+\]/g, `[${rowIndex}]`);
                        }
                        rowGroup.addControl(fieldId, this.createFormControl(fieldCopy, state));
                    }
                }
            });

            arrayControl.push(rowGroup);
        });

        return arrayControl;
    }

    private updatePathsInSection(section: NormalizedSection, rowIndex: number): void {
        // Update fields in the section
        Object.keys(section.fields).forEach((fieldId) => {
            const field = section.fields[fieldId];
            if (field.path) {
                field.path = field.path.replace(/\[\d+\]/g, `[${rowIndex}]`);
            }

            // If it's a section field, recursively update paths
            if (field.type === 'section' && field.section) {
                this.updatePathsInSection(field.section, rowIndex);
            }
        });
    }

    /**
     * Create a FormControl for a field with appropriate validators
     */
    private createFormControl(field: NormalizedFieldState, state: LocationCardState<unknown>): FormControl {
        const validators: ValidatorFn[] = [];
        const asyncValidators: AsyncValidatorFn[] = [];
        let isCustomValidator = false;

        // Add validators based on field configuration
        if ('validators' in field && field.validators.length > 0) {
            field.validators.forEach((rule) => {
                if (rule.type === 'custom') {
                    isCustomValidator = true;
                }

                if (rule.type === ValidationRuleType.AsyncCustom) {
                    const asyncValidator = this.validatorRegistry.getAsyncValidatorByIdentifier(rule.identifier, state);
                    if (asyncValidator) {
                        asyncValidators.push(asyncValidator);
                    }
                } else {
                    const validator = this.createValidator(rule, state);
                    if (validator) {
                        validators.push(validator);
                    }
                }
            });
        }

        const initialValue = field.type === FieldType.Number && (field as NumberField).decimalPlaces != null
            && typeof field.value === 'number' && isFinite(field.value)
            ? (field.value as number).toFixed((field as NumberField).decimalPlaces)
            : field.value;

        const control = this.fb.control(
            { value: initialValue, disabled: !!field.disabled || !state.permissions.isMonitorEditor },
            validators,
            asyncValidators,
        );

        if (isCustomValidator) {
            this.customValidatorFields.push(control);
        }

        if (field.path) {
            // Subscribe to value changes
            const subscription = control.valueChanges.subscribe((newValue) => {
                // Only dispatch the action if we have a valid path
                // Dispatch action to update field value in store using mutation
                this.store.dispatch(
                    updateFieldValueByPath({
                        path: field.path,
                        value: newValue,
                    }),
                );

                this.customValidatorFields.forEach((control) => control.updateValueAndValidity({ emitEvent: false }));
            });

            this.subscriptions.push(subscription);
        }

        if (field.errors && Object.keys(field.errors).length > 0) {
            const errors = Object.keys(field.errors).reduce((acc, key) => {
                acc[key] = true;

                return acc;
            }, {});

            setTimeout(() => {
                control.setErrors(errors);
            }, 0);
        }

        control.markAsTouched();
        return control;
    }
    /**
     * Convert a validation rule to an Angular validator function
     */
    private createValidator(rule: ValidationRule, state: LocationCardState<unknown>): ValidatorFn | null {
        switch (rule.type) {
            case ValidationRuleType.Required:
                return Validators.required;

            case ValidationRuleType.Pattern:
                return rule.value ? Validators.pattern(rule.value) : null;

            case ValidationRuleType.MaxLength:
                return rule.value ? Validators.maxLength(rule.value) : null;

            case ValidationRuleType.MinLength:
                return rule.value ? Validators.minLength(rule.value) : null;

            case ValidationRuleType.Min:
                return rule.value !== undefined ? Validators.min(rule.value) : null;

            case ValidationRuleType.Max:
                return rule.value !== undefined ? Validators.max(rule.value) : null;

            case ValidationRuleType.Custom:
                return this.validatorRegistry.getValidatorByIdentifier(rule.identifier, state);
            default:
                return null;
        }
    }

    /**
     * Update an existing form with new configuration values
     * Useful for partial updates without rebuilding the entire form
     */
    updateFormValues(form: FormGroup, formState: NormalizedFormState): void {
        // Handle section updates recursively
        formState.config.sections.forEach((section) => {
            const sectionControl = form.get(section.id);
            if (!sectionControl) return;

            if (section.type === 'standard') {
                // For standard sections, update each field's value
                Object.entries(section.fields).forEach(([fieldId, field]) => {
                    if (field.type !== 'section') {
                        const control = sectionControl.get(fieldId);
                        if (control && !control.dirty) {
                            const displayValue =
                                field.type === FieldType.Number &&
                                field.decimalPlaces != null &&
                                typeof field.value === FieldType.Number &&
                                isFinite(field.value)
                                    ? field.value.toFixed(field.decimalPlaces)
                                    : field.value;
                            control.setValue(displayValue, { emitEvent: false });

                            // Also update disabled/enabled state if needed
                            if (field.disabled && !control.disabled) {
                                control.disable({ emitEvent: false });
                            } else if (!field.disabled && control.disabled) {
                                control.enable({ emitEvent: false });
                            }
                        }
                    }
                });
            } else if (section.type === 'array') {
                // For array sections, handle each row
                const formArray = sectionControl as FormArray;

                // Handle row count changes
                this.syncFormArrayLength(formArray, section.rows.length);

                // Update each row's field values
                section.rows.forEach((row, rowIndex) => {
                    const rowGroup = formArray.at(rowIndex) as FormGroup;

                    row.fields.forEach((fieldId) => {
                        const field = section.fields[fieldId];
                        if (field && field.type !== 'section') {
                            const control = rowGroup.get(fieldId);
                            if (control && !control.dirty) {
                                const displayValue =
                                    field.type === FieldType.Number &&
                                    field.decimalPlaces != null &&
                                    typeof field.value === FieldType.Number &&
                                    isFinite(field.value)
                                        ? field.value.toFixed(field.decimalPlaces)
                                        : field.value;
                                control.setValue(displayValue, { emitEvent: false });
                            }
                        }
                    });
                });
            }
        });
    }

    /**
     * Ensure a FormArray has the correct number of items
     */
    private syncFormArrayLength(formArray: FormArray, targetLength: number): void {
        const currentLength = formArray.length;

        if (currentLength < targetLength) {
            // Add missing controls
            for (let i = currentLength; i < targetLength; i++) {
                formArray.push(this.fb.group({}));
            }
        } else if (currentLength > targetLength) {
            // Remove extra controls
            for (let i = currentLength - 1; i >= targetLength; i--) {
                formArray.removeAt(i);
            }
        }
    }

    /**
     * Clean up all subscriptions
     */
    public unsubscribeAll(): void {
        this.customValidatorFields = [];
        this.subscriptions.forEach((sub) => sub.unsubscribe());
        this.subscriptions = [];
    }
}
